import React, { useState } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import Link from "next/link";
import { usePathname } from "../../shared/hooks/useNavigation";
import { useRouter } from "../../shared/hooks/useNavigation";
import PropTypes from "prop-types";
import { Nav, Navbar } from "react-bootstrap";
import styled from "styled-components";

import { useAppState } from "../../contexts/ApplicationStateProvider";
import { useStores } from "../../contexts/StoreContext";
import breakpoints from "../../shared/constants";
import useDevice from "../../shared/customHooks/useDevice";
import StyledLink from "./StyledLink";

const StyledNavbarContainer = styled.header`
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.$isOverlayingPage
      ? `
      position: absolute;
      width: 100%;
      `
      : ""}

  ${(props) =>
    props.$navbarPosition
      ? `
      position: ${props.$navbarPosition};
      top: 0;
      left: 0;
      right: 0;
      z-index: 1000;
      `
      : ""}

  ${(props) => props.$dynamicStyles || ""}
`;

const StyledNavbar = styled(Navbar)`
  background: ${({ $background, theme }) =>
    `${$background || theme.primaryColor}`};
  z-index: 12;
  opacity: ${({ $isOpaque, $opacity }) =>
    $isOpaque ? $opacity || 1 : "unset"};
  min-height: auto;

  ${({ $isFullWidth }) =>
    !$isFullWidth
      ? `
      max-width: 1300px;
      margin: auto;
      width: 100%;
      `
      : ""}
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 10px;
  width: 100%;
`;

const StyledLogo = styled(Navbar.Brand)`
  width: ${({ $logoDimensions }) => `${$logoDimensions?.width || 200}px`};
  height: ${({ $logoDimensions }) => `${$logoDimensions?.height || 50}px`};
  max-height: 200px;
  margin-bottom: 20px;
  position: relative;
`;

const StyledLinksWrapper = styled.div`
  width: ${({ $logoDimensions }) => `${$logoDimensions.width || 200}px`};
  height: ${({ $logoDimensions }) => `${$logoDimensions.height || 50}px`};
  padding-left: 10px;
`;

const VerticalDropdownWrapper = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  left: -50%;
  min-width: 200px;
  background: ${({ theme, $navigationBackgroundColor }) =>
    $navigationBackgroundColor || theme.primaryColor};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 0.5rem 0;
`;

const VerticalDropdownItem = styled.div`
  padding: 0.75rem 1rem;
  color: ${({ theme }) => theme.textColor};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const StyledToggle = styled(Navbar.Toggle)`
  border: none !important;
  outline: none !important;
  box-shadow: none !important;

  display: ${({ $isMobile }) => ($isMobile ? "unset !important" : "none")};
`;

const StyledMenuItem = styled(Nav.Item)`
  position: relative;
  font-size: 1rem;
  z-index: 1;

  border-top: ${({ $isMobile }) =>
    $isMobile ? "1px solid hsla(0,0%,100%,.7)" : "none"};

  width: ${({ $isMobile }) => ($isMobile ? "100%" : "unset")};

  &:hover ${VerticalDropdownWrapper} {
    display: block;
  }

  @media (max-width: ${breakpoints.DESKTOP}) {
    justify-content: center;
  }
`;

const StyledReturnButton = styled.button`
  border: none;
  outline: none;
  background: none;
  font-size: 1.2rem;
  position: relative;
  color: ${({ theme }) => theme.textColor};

  &:hover,
  &:active {
    color: ${({ theme }) => theme.primaryColor};
  }
`;

const StyledNavLink = styled.div`
  #vertical-dropdown-nav-wrapper {
    display: ${({ $isMobile }) => ($isMobile ? "none" : "unset")};
  }

  .show #vertical-dropdown-nav-wrapper {
    display: ${({ $isMobile }) => ($isMobile ? "block" : "unset")};
  }
`;

const StyledCollapse = styled(Navbar.Collapse)`
  position: ${({ $isMobile }) => ($isMobile ? "absolute" : "unset")};
  right: ${({ $isMobile }) => ($isMobile ? "0" : "unset")};
  left: ${({ $isMobile }) => ($isMobile ? "0" : "unset")};
  top: ${({ $isMobile }) => ($isMobile ? "100%" : "unset")};
`;

const Wrapper = styled.div`
  height: ${({ $isMobile }) => ($isMobile ? "100vh" : "100%")};
  left: ${({ $isMobile }) => ($isMobile ? "0" : "unset")};
  top: ${({ $isMobile }) => ($isMobile ? "0" : "unset")};
  width: ${({ $isMobile }) => ($isMobile ? "100%" : "unset")};
  background: ${({ $isMobile, $navigationBackgroundColor }) => {
    let color = "unset";
    if ($isMobile) {
      if ($navigationBackgroundColor) {
        color = $navigationBackgroundColor;
      } else {
        color = "rgba(22, 25, 26, 0.95)";
      }
    }
    return $navigationBackgroundColor || color;
  }};
  overflow: ${({ $isMobile }) => ($isMobile ? "auto" : "unset")};
  display: ${({ $isMobile }) => ($isMobile ? "flex" : "unset")};
  flex-direction: ${({ $isMobile }) => ($isMobile ? "column" : "unset")};
  padding: ${({ $isMobile }) => ($isMobile ? "30px 30px 50px" : "unset")};
  justify-content: ${({ $isMobile }) => ($isMobile ? "center" : "unset")};
  position: ${({ $isMobile }) => ($isMobile ? "absolute" : "unset")};
`;

function VerticalDropdownNavLinks(props) {
  const { navigationBackgroundColor, className, onLinkClick } = props;
  const router = useRouter();
  const currentPath = usePathname();
  const { isMobile, isSmallScreen } = useDevice();

  const { setIsInteractive, isInteractive } = useAppState();
  const { navigationStore, homepageStore } = useStores();
  const { topNavigation } = navigationStore;
  const { hasVideos } = homepageStore;

  const [selectedParent, setSelectedParent] = useState(null);

  const handleNavigation = (href) => {
    if (onLinkClick) {
      onLinkClick();
    }
    router.push(href);
  };

  const returnToStatic = () => {
    setIsInteractive(false);
    router.push("/");
  };

  if (isEmpty(topNavigation)) {
    return null;
  }

  const handleClickParent = (parentItem) => {
    setSelectedParent(parentItem);
  };

  const handleClickBack = (e) => {
    setSelectedParent(null);
    e.stopPropagation();
    e.preventDefault();
    return false;
  };

  const renderMobileMenu = () => {
    if (selectedParent) {
      return (
        <StyledMenuItem
          className={`m-lg-4 text-black ${
            selectedParent?.children && selectedParent?.children.length > 0
              ? "has-children"
              : ""
          } ${selectedParent?.isHeaderItem ? "is-header-item" : ""}`}
          id="second-level-navigation-menu-selected"
          $isMobile={isMobile || isSmallScreen}
        >
          <StyledReturnButton onClick={(e) => handleClickBack(e)}>
            <Image
              alt="go back a page"
              height={20}
              objectFit="contain"
              src="/arrow-border-left-white.svg"
              width={20}
            />
            <span className="fs-1 ps-5">{selectedParent.title}</span>
          </StyledReturnButton>
          {selectedParent.children.map((child, index) => (
            <StyledLink
              key={child.id}
              className="text-uppercase"
              eventKey={`${child.path}-${index}`}
              href={child.path ? `/${child.path}` : (child.externalPath || "/")}
              isMobile
              page={child}
              onClick={
                child.isHeaderItem
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  : () =>
                      handleNavigation(
                        child.path ? `/${child.path}` : (child.externalPath || "/")
                      )
              }
            >
              {child.title}
            </StyledLink>
          ))}
        </StyledMenuItem>
      );
    }

    return topNavigation.map((child, index) => {
      const href = child.path ? `/${child.path}` : (child.externalPath || "/");
      return (
        <StyledMenuItem
          key={child.id}
          id="second-level-navigation-menu"
          $isMobile={isMobile || isSmallScreen}
          className={`ms-lg-4 ${
            child.children && child.children.length > 0 ? "has-children" : ""
          }  ${child.isHeaderItem ? "is-header-item" : ""}`}
        >
          <StyledLink
            active={currentPath.includes(child.path)}
            eventKey={`${child.path}-${index}`}
            href={href}
            isMobile
            navigationBackgroundColor={navigationBackgroundColor}
            onClick={(e) => {
              if (child.isHeaderItem) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              if (child.children.length > 0) {
                handleClickParent(child);
                return;
              }
              handleNavigation(href);
            }}
            page={child}
          >
            {child.title}
          </StyledLink>
        </StyledMenuItem>
      );
    });
  };

  const renderDesktopMenu = () => {
    return topNavigation.map((page, index) => {
      const href = page.path ? `/${page.path}` : (page.externalPath || "/");

      return (
        <StyledMenuItem
          key={`${href}-${page?.title}`}
          className={`ms-lg-4 ${
            page.children && page.children.length > 0 ? "has-children" : ""
          }
          ${page.isHeaderItem ? "is-header-item" : ""}
          `}
          id="navigation-menu-desktop"
          $isMobile={isMobile || isSmallScreen}
        >
          <StyledLink
            active={currentPath.includes(page.path)}
            className="text-uppercase"
            eventKey={`${page.path}-${index}`}
            href={href}
            navigationBackgroundColor={navigationBackgroundColor}
            onClick={
              page.isHeaderItem || page.disable_route
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                : undefined
            }
            page={page}
          >
            {page.title}
          </StyledLink>
          {page.children && page.children.length > 0 && (
            <VerticalDropdownWrapper
              id="vertical-dropdown-menu-desktop"
              $navigationBackgroundColor={navigationBackgroundColor}
            >
              {page.children.map((subItem) => {
                const childHref = subItem.path
                  ? `/${subItem.path}`
                  : (subItem.externalPath || "/");

                return (
                  <VerticalDropdownItem key={`${subItem.path}`}>
                    <StyledLink
                      active={currentPath.includes(subItem.path)}
                      className={`text-uppercase h-100 w-100 ${
                        subItem.isHeaderItem ? "is-header-item" : ""
                      }`}
                      eventKey={`${subItem.path}-${index}`}
                      href={childHref}
                      navigationBackgroundColor={navigationBackgroundColor}
                      onClick={
                        subItem.isHeaderItem
                          ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          : undefined
                      }
                      page={subItem}
                    >
                      {subItem.title}
                    </StyledLink>
                  </VerticalDropdownItem>
                );
              })}
            </VerticalDropdownWrapper>
          )}
        </StyledMenuItem>
      );
    });
  };

  return (
    <StyledNavLink
      className={className}
      $isMobile={isMobile || isSmallScreen}
      id="vertical-dropdown-nav-link-wrapper"
    >
      <StyledToggle
        aria-controls="vertical-dropdown-collapse"
        className="h-100"
        $isMobile={isMobile || isSmallScreen}
        id="vertical-dropdown-toggle"
      />
      <StyledCollapse
        className="justify-content-end h-100"
        id="vertical-dropdown-collapse"
        $isMobile={isMobile || isSmallScreen}
      >
        <Wrapper
          id="vertical-dropdown-nav-wrapper"
          $isMobile={isMobile || isSmallScreen}
          $navigationBackgroundColor={navigationBackgroundColor}
        >
          <Nav
            className={`${
              isMobile || isSmallScreen
                ? "d-flex flex-column"
                : "d-flex justify-content-center"
            } h-100`}
            id="vertical-dropdown-nav"
          >
            {isMobile || isSmallScreen
              ? renderMobileMenu()
              : renderDesktopMenu()}

            {isMobile && isInteractive && hasVideos && (
              <StyledMenuItem
                className="ms-lg-4"
                $isMobile={isMobile || isSmallScreen}
              >
                <StyledReturnButton className="pt-5" onClick={returnToStatic}>
                  Take me to the normal site
                </StyledReturnButton>
              </StyledMenuItem>
            )}
          </Nav>
        </Wrapper>
      </StyledCollapse>
    </StyledNavLink>
  );
}

function VerticalDropdownNavigation(props) {
  const { settings, customLogo, backgroundColour } = props;
  const { configStore, navigationStore } = useStores();
  const { config } = configStore;
  const { isCountryCodeNavigationUsed } = navigationStore;
  const { isMobile, isSmallScreen } = useDevice();
  const [expanded, setExpanded] = useState(false);

  let logoUrl = config?.logo?.url;
  let logoHeight = config?.logoHeight;
  let logoWidth = config?.logoWidth;

  if (customLogo?.url) {
    logoUrl = customLogo?.url;
    logoHeight = customLogo?.height;
    logoWidth = customLogo?.width;
  }

  return (
    <StyledNavbarContainer
      $dynamicStyles={settings?.CSS}
      id="vertical-dropdown-navigation"
      $isOverlayingPage={settings?.isOverlayingPage}
      $navbarPosition={settings?.position}
    >
      <StyledNavbar
        $background={backgroundColour || settings?.background}
        $isOpaque={settings?.isOpaque || false}
        $opacity={settings?.opacity || 0}
        style={settings?.styles}
        $isFullWidth={settings?.fullWidth}
        $logoHeight={logoHeight}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        id="vertical-dropdown-navbar"
      >
        <StyledHeader
          $isMobile={isMobile || isSmallScreen}
          className="w-100"
          id="vertical-dropdown-header"
          $logoDimensions={{
            height: logoHeight,
            width: logoWidth,
          }}
        >
          {logoUrl && (
            <StyledLogo
              className="header-logo"
              id="vertical-dropdown-logo"
              $logoDimensions={{
                height: logoHeight,
                width: logoWidth,
              }}
            >
              <Link href={config.logoUrl || "/"}>
                <Image
                  alt={config.siteName}
                  layout="fill"
                  objectFit="contain"
                  priority
                  src={logoUrl}
                />
              </Link>
            </StyledLogo>
          )}
          <VerticalDropdownNavLinks
            className="h-100"
            id="vertical-dropdown-nav-links"
            navigationBackgroundColor={backgroundColour || settings?.background}
            onLinkClick={() => setExpanded(false)}
          />
          {isCountryCodeNavigationUsed && (
            <StyledLinksWrapper
              $logoDimensions={{
                height: logoHeight,
                width: logoWidth,
              }}
            >
              {/* CountryCodeNavigation component would go here if needed */}
            </StyledLinksWrapper>
          )}
        </StyledHeader>
      </StyledNavbar>
    </StyledNavbarContainer>
  );
}

VerticalDropdownNavigation.defaultProps = {
  customLogo: null,
  settings: null,
};

VerticalDropdownNavigation.propTypes = {
  customLogo: PropTypes.shape({}),
  settings: PropTypes.shape({
    CSS: PropTypes.string,
    background: PropTypes.string,
    isOpaque: PropTypes.bool,
    opacity: PropTypes.number,
    fullWidth: PropTypes.bool,
    isOverlayingPage: PropTypes.bool,
    styles: PropTypes.shape({}),
  }),
};

export default observer(VerticalDropdownNavigation);
